import { SelectCompany } from '@finch-api/common/dist/internal/connect/authorize';

// Entity lists longer than this get a filter input; shorter ones are faster to
// scan than to type into.
export const FILTER_THRESHOLD = 8;

// The API sends entities as `{ companyName }` with no identifier, and accountant
// lists do contain repeated names, so a row's position in the unfiltered list is
// the only thing that identifies it unambiguously.
export type EntityRow<T> = {
  account: T;
  index: number;
};

const compareByCompanyName = (a: SelectCompany, b: SelectCompany) =>
  a.companyName.localeCompare(b.companyName, undefined, {
    numeric: true,
    sensitivity: 'base',
  });

export const sortAccountsByName = <T extends SelectCompany>(
  accounts: T[],
): T[] => {
  const sorted = [...accounts];

  sorted.sort(compareByCompanyName);

  return sorted;
};

export const buildEntityRows = <T extends SelectCompany>(
  accounts: T[],
): EntityRow<T>[] => {
  const rows: EntityRow<T>[] = [];

  for (let index = 0; index < accounts.length; index += 1) {
    rows.push({ account: accounts[index], index });
  }

  rows.sort((a, b) => compareByCompanyName(a.account, b.account));

  return rows;
};

export const filterEntityRows = <T extends SelectCompany>(
  rows: EntityRow<T>[],
  filter: string,
): EntityRow<T>[] => {
  const term = filter.trim().toLowerCase();

  if (!term) {
    return rows;
  }

  const matches: EntityRow<T>[] = [];

  for (const row of rows) {
    if (row.account.companyName.toLowerCase().includes(term)) {
      matches.push(row);
    }
  }

  return matches;
};
